import React, { useEffect, useState } from 'react';
import { BaseLayout } from 'layouts';
import { PassiveBanner } from '../../features/passive/PassiveBanner';
import { PassivePrograms } from '../../features/passive/PassivePrograms';
import { Top30Table } from '../../features/passive/Top30Table';
import { useWeb3React } from '@web3-react/core';
import { getPublicRpcProvider, usePassiveIncome } from '../../helpers/hooks/usePassiveIncome';
import { Contract } from '@ethersproject/contracts';
import { pollyTokenAbi } from '../../helpers/contractsAbi/pollyToken';
import DuoIcon from '../../assets/icons/dashboard/programs/programCard/duoIcon.svg';
import QuattroIcon from '../../assets/icons/dashboard/programs/programCard/quattroIcon.svg';
import PollyIcon from '../../assets/icons/main/pollyCoin.svg';
import { PROGRAM_NAMES } from '../../helpers/constants';
import { formatNumberWithSpaces } from '../../helpers/numbers';
import { usePreviewMode } from '../../helpers/hooks/useCheckPreviewMode';
import { Pulse } from '../../features/passive/Pulse';

const POLLY_TOKEN_ADDRESS = '0x53A7196EFf4049D1049574EBB0F4a10376913a3D';

export const WeeklyReward = () => {
  const { account, provider } = useWeb3React();
  const { previewUserAddress } = usePreviewMode();
  const { getPassiveData, passiveData, isLoading } = usePassiveIncome();
  const [contractBalances, setContractBalances] = useState({
    x2: null,
    x4: null,
  });

  const userAddress = previewUserAddress || account || null;
  const showSkeleton = isLoading && !passiveData;

  useEffect(() => {
    getPassiveData(userAddress);
  }, [userAddress, account, provider, getPassiveData]);

  useEffect(() => {
    const fetchContractBalances = async () => {
      if (!passiveData) return;

      const signerOrProvider =
        account && provider ? provider.getSigner(account).connectUnchecked() : getPublicRpcProvider();

      if (!signerOrProvider) return;

      try {
        const pollyContract = new Contract(POLLY_TOKEN_ADDRESS, pollyTokenAbi, signerOrProvider);

        const balances = {
          x2: null,
          x4: null,
        };

        if (passiveData.x2?.contractAddress) {
          const balanceX2 = await pollyContract.balanceOf(passiveData.x2.contractAddress);
          balances.x2 = Number(balanceX2.toString()) / 1e18;
        }

        if (passiveData.x4?.contractAddress) {
          const balanceX4 = await pollyContract.balanceOf(passiveData.x4.contractAddress);
          balances.x4 = Number(balanceX4.toString()) / 1e18;
        }

        setContractBalances(balances);
      } catch (error) {
        console.error('Error fetching contract balances:', error);
      }
    };

    fetchContractBalances();
  }, [passiveData, provider, account]);

  const updatePassiveData = () => {
    getPassiveData(userAddress);
  };

  return (
    <BaseLayout className="lg:!bg-[#0C0C0E]">
      <div className="flex flex-col space-y-[2rem] w-full lg:space-y-[2.4rem] lg:px-[1.6rem]">
        <PassiveBanner />
        {/* weekly balance duo and quattro */}
        <div className="flex items-center space-x-[2rem] w-full lg:flex-col lg:space-x-0 lg:space-y-[1.2rem]">
          <div className="flex-1 flex items-center justify-between rounded-[3.2rem] bg-white-40 border-[0.1rem] border-solid border-white-50 p-[2rem] backdrop-blur-[1.5rem] lg:w-full lg:rounded-[2rem]">
            <div className="flex items-center space-x-[1.2rem]">
              <img className="w-[2.8rem] h-[2.8rem]" src={DuoIcon} />
              <span className="text-[1.8rem] font-600 text-white lg:text-[1.6rem]">{PROGRAM_NAMES.X2}</span>
            </div>
            <div className="flex items-center space-x-[0.8rem]">
              {showSkeleton || contractBalances.x2 == null ? (
                <Pulse className="h-[2.4rem] w-[8rem]" />
              ) : (
                <span className="text-[2rem] font-600 text-white lg:text-[1.8rem]">
                  {formatNumberWithSpaces(contractBalances.x2)}
                </span>
              )}
              <img className="w-[2rem] h-[2rem]" src={PollyIcon} />
            </div>
          </div>
          <div className="flex-1 flex items-center justify-between rounded-[3.2rem] bg-white-40 border-[0.1rem] border-solid border-white-50 p-[2rem] backdrop-blur-[1.5rem] lg:w-full lg:rounded-[2rem]">
            <div className="flex items-center space-x-[1.2rem]">
              <img className="w-[2.8rem] h-[2.8rem]" src={QuattroIcon} />
              <span className="text-[1.8rem] font-600 text-white lg:text-[1.6rem]">{PROGRAM_NAMES.X4}</span>
            </div>
            <div className="flex items-center space-x-[0.8rem]">
              {showSkeleton || contractBalances.x4 == null ? (
                <Pulse className="h-[2.4rem] w-[8rem]" />
              ) : (
                <span className="text-[2rem] font-600 text-white lg:text-[1.8rem]">
                  {formatNumberWithSpaces(contractBalances.x4)}
                </span>
              )}
              <img className="w-[2rem] h-[2rem]" src={PollyIcon} />
            </div>
          </div>
        </div>
        <PassivePrograms isLoading={showSkeleton} passiveData={passiveData} updatePassiveData={updatePassiveData} />
        <Top30Table isLoading={showSkeleton} passiveData={passiveData} />
      </div>
    </BaseLayout>
  );
};
